import React, { useState } from "react";
import ReactTable from "react-table";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  UncontrolledTooltip
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { ReporteAsesorResumen } from "../reporteAsesor/ReporteAsesorResumen.cards";
import s from "./styles.scss";
import MembresiaSnapshot from "../../../../types/MembresiaSnapshot";
import { formatIsoDate, formatMoney } from "../../../helpers/formatters";

interface Props {
  reporte: any;
  loading: boolean;
}

export const ReporteLiderTable = ({ reporte, loading }: Props) => {
  const [columns] = useState(operacionesColumns());

  if (!reporte || !reporte.membresias) {
    return null;
  }

  return (
    <div>
      <h5 className="mb-3">Club {reporte.club}</h5>

      {reporte.membresias.map((asesor: any) => (
        <Card key={asesor.idVendedor} className="mb-4">
          <CardHeader>
            <Row className="align-items-center">
              <Col>
                <strong>
                  {asesor.nombreVendedor || `Asesor ${asesor.idVendedor}`}
                </strong>
                {asesor.numeroEmpleadoVendedor && (
                  <span className="text-muted ml-2">
                    ({asesor.numeroEmpleadoVendedor})
                  </span>
                )}
              </Col>
              <Col xs="auto">
                {asesor.resumen ? (
                  <Badge color="primary">En plantilla</Badge>
                ) : (
                  <Badge color="secondary">Venta externa</Badge>
                )}
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            {asesor.resumen && (
              <ReporteAsesorResumen
                resumen={asesor.resumen}
                loading={loading}
              />
            )}

            <ReactTable
              data={asesor.operaciones || []}
              columns={columns}
              loading={loading}
              pageSize={
                asesor.operaciones && asesor.operaciones.length > 5
                  ? asesor.operaciones.length
                  : 5
              }
              showPagination={false}
              className="-striped -highlight"
              loadingText="Cargando..."
              noDataText="Sin operaciones para este club"
            />
          </CardBody>
        </Card>
      ))}
    </div>
  );
};

const operacionesColumns = () => [
  {
    Header: "Cotización",
    id: "idCotizacionItem",
    className: s.tdClear,
    headerClassName: "text-left",
    width: 80,
    accessor: simpleAccessor("idCotizacionItem")
  },
  {
    Header: "Periodo",
    id: "periodo",
    className: s.tdClear,
    headerClassName: "text-left",
    width: 100,
    accessor: dateAccessor("periodo")
  },
  {
    Header: "Factura",
    id: "folioFactura",
    className: s.tdClear,
    headerClassName: "text-left",
    width: 96,
    accessor: simpleAccessor("folioFactura")
  },
  {
    Header: "Operación",
    id: "tipoOperacion",
    className: s.tdClear,
    headerClassName: "text-left",
    width: 104,
    accessor: (row: MembresiaSnapshot) => {
      switch (row.tipoOperacion) {
        case "VENTA_MEMBRESIA":
          return <div className={fieldClassName(row)}>Venta</div>;
        case "REACTIVACION":
          return <div className={fieldClassName(row)}>Reactivación</div>;
        case "INTEGRANTE_INDIVIDUAL":
          return <div className={fieldClassName(row)}>Integrante</div>;
        default:
          return <div className={fieldClassName(row)}>No reconocida</div>;
      }
    }
  },
  {
    Header: "Membresía",
    id: "membresiaLink",
    className: s.tdClear,
    headerClassName: "text-left",
    accessor: (row: MembresiaSnapshot) => {
      const href = `/membresias-socios/${row.idMembresiaSocio}?idClub=${row.idClubBase}&idPublico=${row.idPublico}`;
      const label = `${row.idPublico} de ${row.clubBase}`;
      return (
        <div className={fieldClassName(row)}>
          <a href={href} target="_blank" rel="noreferrer">
            {label}
          </a>
        </div>
      );
    }
  },
  {
    Header: "Tipo",
    id: "tipoMembresia",
    className: s.tdClear,
    headerClassName: "text-left",
    width: 104,
    accessor: (row: MembresiaSnapshot) => (
      <div className={fieldClassName(row)}>
        {(row.membresia || "NA").replace("Membresía ", "")}&nbsp;
      </div>
    )
  },
  {
    Header: "Precio Venta",
    id: "precioVenta",
    className: s.tdClear,
    headerClassName: "text-left",
    width: 100,
    accessor: moneyAccessor("precioVenta")
  },
  {
    Header: "Alcance",
    id: "cuentaAlcance",
    className: s.tdClear,
    headerClassName: "text-left",
    width: 100,
    accessor: (row: MembresiaSnapshot) => {
      const btnId = `lider-venta-${row.idCotizacionItem}`;

      // Si cuenta para alcance pero tiene 0 puntos, mostrar razón en amarillo
      if (row.cuentaAlcance && row.puntosAlcance === 0) {
        return (
          <div className={fieldClassName(row)}>
            {renderRazones(
              false,
              ["El Plan Parcial Vespertino no añade puntos de alcance"],
              `${btnId}-cuentaAlcance`,
              "#ac992d"
            )}
          </div>
        );
      }

      return (
        <div className={fieldClassName(row)}>
          {row.cuentaAlcance
            ? "Sí"
            : renderRazones(
                row.cuentaAlcance,
                row.cuentaAlcanceRazones,
                `${btnId}-cuentaAlcance`
              )}
        </div>
      );
    }
  },
  {
    Header: "Comisionable",
    id: "comisionable",
    className: s.tdClear,
    headerClassName: "text-left",
    width: 100,
    accessor: (row: MembresiaSnapshot) => {
      const btnId = `lider-venta-${row.idCotizacionItem}`;
      return (
        <div className={fieldClassName(row)}>
          {row.cuentaComision
            ? "Sí"
            : renderRazones(
                row.cuentaComision,
                row.cuentaComisionRazones,
                `${btnId}-cuentaComision`
              )}
        </div>
      );
    }
  },
  {
    Header: "Cump. Reglas",
    id: "cumplReglas",
    className: s.tdClear,
    headerClassName: "text-left",
    width: 100,
    accessor: (row: MembresiaSnapshot) => {
      const btnId = `lider-venta-${row.idCotizacionItem}`;
      return (
        <div className={fieldClassName(row)}>
          {row.cuentaReglas
            ? "Sí"
            : renderRazones(
                row.cuentaReglas,
                row.cuentaReglasRazones,
                `${btnId}-cuentaReglas`,
                !row.cuentaAlcance || !row.cuentaComision
                  ? "#ffffff"
                  : "#8b0000"
              )}
        </div>
      );
    }
  }
];

function fieldClassName(row: MembresiaSnapshot) {
  let className = s.field;

  if (!row.cuentaAlcance || !row.cuentaComision) {
    className += ` ${s.incumplimiento}`;
    return className;
  }

  if (!row.cuentaReglas) {
    className += ` ${s.facturaCancelada}`;
  }

  if (row.tieneCambios) {
    className += ` ${s.conCambios}`;
  } else if (row.evaluadorModifico) {
    className += ` ${s.conActualizaciones}`;
  }

  return className;
}

function simpleAccessor(key: keyof MembresiaSnapshot) {
  return (row: MembresiaSnapshot) => (
    <div className={fieldClassName(row)}>{row[key]}&nbsp;</div>
  );
}

function dateAccessor(key: keyof MembresiaSnapshot) {
  return (row: MembresiaSnapshot) => {
    const fecha = row[key] ? formatIsoDate(row[key] as string) : "";
    return <div className={fieldClassName(row)}>{fecha}&nbsp;</div>;
  };
}

function moneyAccessor(accessor: string) {
  return (row: MembresiaSnapshot) => {
    const cantidad = Number(row[accessor]);
    return (
      <div className={fieldClassName(row) + " text-right"}>
        {!Number.isNaN(cantidad) && row[accessor] != null
          ? formatMoney(cantidad, 2)
          : "-"}
      </div>
    );
  };
}

function renderRazones(
  cumplimiento: boolean,
  razones: string[],
  id: string,
  color?: string
) {
  if (cumplimiento || razones == null) {
    return null;
  }

  const tooltip =
    razones.length > 0
      ? razones.map((razon, idx) => <div key={idx}>• {razon}</div>)
      : "No cumple con los requisitos de comisionabilidad";

  return (
    <span className={s.info} id={id}>
      <FontAwesomeIcon icon={faExclamationCircle} color={color || "#ffffff"} />
      <UncontrolledTooltip placement="left" target={id}>
        <div style={{ textAlign: "left" }}>
          <strong>Incumplimiento:</strong>
          {tooltip}
        </div>
      </UncontrolledTooltip>
    </span>
  );
}
